/**
 * Where the operated-camera declaration stands.
 *
 * It is the one criterion nothing in the recording can settle — phase
 * correlation could not tell an operated camera from a static one — so the
 * producer declares it during approval. What happens next depends entirely on
 * whether the declaration is worth money:
 *
 * - **A declaration of none is finished when it is made.** It costs the
 *   producer the whole camera fee, so nobody claims it falsely and there is
 *   nothing for an admin to agree to. It is not work, it is not on a list,
 *   and there is no button on it.
 * - **A claim that pays is somebody's word about 825 or 1650 crowns**, and
 *   the money already followed it: `computeReward` reads
 *   `declarations.operatedCamera` and nothing else, so by the time the
 *   producer approved, the claim was paid. Confirming is the admin's record
 *   that they looked — not a condition of payment, which would restate
 *   amounts already agreed across a season that is almost entirely approved.
 *
 * Disputing is the other answer, and unlike confirming it applies to any
 * declaration, none included: a producer who under-claims is owed the news
 * as much as one who over-claims. It moves no money either — on an approved
 * game the amount is agreed — so it records the disagreement and puts it in
 * front of the producer as a comment. On a game not yet approved the fix is
 * the one that already exists: take it back for editing and let them declare
 * again.
 */
import {EvaluationGame} from '../model/evaluation/evaluation';

export type DeclarationState =
/** The producer has not said yet. Only possible before they approve. */
  'undeclared'
  /** Declared as none. Nothing to confirm, and never on a work list. */
  | 'none'
  /** A claim worth money that nobody has checked. This is the work. */
  | 'toConfirm'
  /** An admin has put their name to it. */
  | 'confirmed'
  /** An admin does not accept it, and said why. */
  | 'disputed';

export const DECLARATION_LABELS: { [value: string]: string } = {
  '0': 'None',
  '0.5': 'Not on live play',
  '1': 'On live play',
};

export function declaredValue(game: EvaluationGame): number | null {
  const value = game?.declarations?.operatedCamera;
  return value === null || value === undefined ? null : value;
}

export function declarationLabel(game: EvaluationGame): string {
  const value = declaredValue(game);
  return value === null ? '—' : DECLARATION_LABELS[String(value)] ?? `${value}`;
}

/** Whether the declaration is worth money, and so worth an admin's word. */
export function declarationPays(game: EvaluationGame): boolean {
  return (declaredValue(game) ?? 0) > 0;
}

export function declarationState(game: EvaluationGame): DeclarationState {
  // Disputed is read first: it is the one state somebody still has to settle,
  // and it can sit on a none as easily as on a claim.
  if (game?.declarations?.disputedAt) {
    return 'disputed';
  }
  // Only ever set on a claim that pays now, but older data may carry a
  // confirmed none — showing that as unconfirmed would deny something that
  // actually happened.
  if (game?.declarations?.confirmedAt) {
    return 'confirmed';
  }
  // `declaredAt` rather than the value: a producer who picks None is saying
  // something, and that has to read differently from never having answered.
  if (!game?.declarations?.declaredAt) {
    return 'undeclared';
  }
  return declarationPays(game) ? 'toConfirm' : 'none';
}

/** Whether an admin has anything to agree to. A none gives them nothing. */
export function needsConfirming(game: EvaluationGame): boolean {
  return declarationState(game) === 'toConfirm';
}
